import { useState } from 'react';
import { Select } from 'antd';
import { M } from '../shared/constants';

// ── Section card wrapper ──────────────────────────────────────────────────────
export function Section({ title, icon, children, style }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, overflow: 'hidden', ...style }}>
      {title && (
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon && <span style={{ color: M, fontSize: 14 }}>{icon}</span>}
          <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</span>
        </div>
      )}
      <div style={{ padding: '12px 16px' }}>{children}</div>
    </div>
  );
}

// ── Info row ──────────────────────────────────────────────────────────────────
export function InfoRow({ label, value, icon }) {
  if (!value || value === 'null') return null;
  return (
    <div style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #f9fafb' }}>
      {icon && <span style={{ color: '#9ca3af', fontSize: 13, width: 16, flexShrink: 0, marginTop: 1 }}>{icon}</span>}
      <span style={{ fontSize: 12, color: '#6b7280', minWidth: 110, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: '#111827', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

// ── Grid of info fields ───────────────────────────────────────────────────────
export function InfoGrid({ fields, cols = 2 }) {
  const visible = fields.filter(f => f.value && String(f.value) !== 'null' && f.value !== '—');
  if (!visible.length) return <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>कोई जानकारी उपलब्ध नहीं</div>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${cols === 1 ? '100%' : '160px'}, 1fr))`, gap: '12px 16px' }}>
      {visible.map(({ label, value }) => (
        <div key={label} style={{ borderBottom: '1px solid #f3f4f6', paddingBottom: 8 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 3 }}>{label}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', wordBreak: 'break-word' }}>{value}</div>
        </div>
      ))}
    </div>
  );
}

// ── Section header (dark, like admin view) ────────────────────────────────────
export function SectionHeader({ title, icon }) {
  return (
    <div style={{
      background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
      padding: '8px 16px',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span style={{ color: M, fontSize: 13 }}>{icon}</span>
      <span style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', letterSpacing: 0.5, textTransform: 'uppercase' }}>{title}</span>
    </div>
  );
}

// ── Profile section bar (with edit/save/cancel) ───────────────────────────────
export function SectionBar({ title, icon, editing: isEditing, onEdit, onCancel, onSave, saving: isSaving }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '8px 14px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
      <span style={{ fontWeight: 700, fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
        {icon && <span style={{ color: M }}>{icon}</span>}{title}
      </span>
      <span style={{ display: 'flex', gap: 6 }}>
        {isEditing ? (
          <>
            <button onClick={onCancel} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6,
              border: '1px solid #d1d5db', cursor: 'pointer', background: '#fff', color: '#6b7280' }}>रद्द करें</button>
            <button onClick={onSave} disabled={isSaving} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6,
              border: 'none', cursor: 'pointer', background: M, color: '#FFD700', fontWeight: 700 }}>
              {isSaving ? 'सेव हो रहा…' : 'सेव करें'}
            </button>
          </>
        ) : (
          <button onClick={onEdit} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6,
            border: `1px solid ${M}`, cursor: 'pointer', background: '#fff', color: M, fontWeight: 600 }}>
            ✏️ बदलें
          </button>
        )}
      </span>
    </div>
  );
}

// ── Profile form field ────────────────────────────────────────────────────────
export function FField({ label, value, onChange, type = 'text', options }) {
  if (type === 'select') {
    return (
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
        <Select value={value || undefined} onChange={onChange} style={{ width: '100%' }} size="small"
          placeholder={label}>
          {(options || []).map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
        </Select>
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', height: 34, borderRadius: 7, border: '1px solid #d1d5db',
          padding: '0 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fafbff' }}
        onFocus={e => e.target.style.borderColor = M}
        onBlur={e => e.target.style.borderColor = '#d1d5db'}
      />
    </div>
  );
}

// ── Editable field row ────────────────────────────────────────────────────────
export function EditableField({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <input type={type} value={value || ''}
        onChange={e => onChange(e.target.value)}
        style={{ width: '100%', height: 36, borderRadius: 7, border: '1px solid #d1d5db',
          padding: '0 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box',
          background: '#fafbff' }}
        onFocus={e => e.target.style.borderColor = M}
        onBlur={e => e.target.style.borderColor = '#d1d5db'}
      />
    </div>
  );
}
